/**
 * Auth token store for TanStack DB write-through handlers.
 *
 * Problem: Collection `onInsert`/`onUpdate`/`onDelete` handlers run deep
 * in the TanStack DB stack and don't receive React context. They still
 * need an access token to call `createServerFn` endpoints.
 *
 * Solution: A tiny module-level store that the UI layer sets whenever
 * the session changes. Handlers read the current token synchronously.
 *
 * Usage in a component:
 *   const { session } = useAuth();
 *   useEffect(() => {
 *     if (session?.access_token) setAccessToken(session.access_token);
 *   }, [session]);
 *
 * Staleness risk: Supabase JWTs expire after ~1h. If the UI doesn't
 * re-render before a write-through handler fires, the token may be
 * stale and the server returns 401. The TTL check below makes this
 * fail loudly so the caller can surface an auth error instead of
 * silently failing.
 */

let _accessToken: string | null = null;
let _tokenSetAt = 0;

const TOKEN_TTL_MS = 50 * 60 * 1000; // 50 minutes (margin before 1h expiry)

export function setAccessToken(token: string | null) {
  _accessToken = token;
  _tokenSetAt = token ? Date.now() : 0;
}

export function getAccessToken(): string | null {
  if (!_accessToken) return null;
  if (Date.now() - _tokenSetAt > TOKEN_TTL_MS) {
    // Token likely expired; clear it so callers get a clean null
    _accessToken = null;
    _tokenSetAt = 0;
    return null;
  }
  return _accessToken;
}
